import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Checkbox, Input, Radio, Select } from 'antd';
import PhoneInput from 'react-phone-input-2';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { useEdges, useNodes } from '@xyflow/react';
import type { AgentStructuredQuestion } from '@/apis/logiblocksAgentAPI';
import TeamUserSelect, {
  type TeamData,
  type TeamUserSelection,
} from '@/components/TeamUserSelect';
import {
  BrackedAddIcon,
  InboundIcon,
  OutboundIcon,
} from '@/assets/icons/icons';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import { TemplatedTextArea } from '../variables/TemplatedTextArea/TemplatedTextArea';
import type { ReactFlowEdge, ReactFlowNode } from '../types';
import { applyGraphOps } from './applyGraphOps';
import { parseCompilerRawOps, readGraphOpsVersion } from './graphOps';
import 'react-phone-input-2/lib/style.css';
import styles from './ClarifyingQuestionCard.module.css';

interface Props {
  question: AgentStructuredQuestion;
  disabled?: boolean;
  exiting?: boolean;
  onSubmit: (values: string[], templateChunks?: TextTemplateChunk[]) => void;
  initialAnswer?: { values: string[]; templateChunks?: TextTemplateChunk[] };
}

function DynamicTemplateAnswer({
  question,
  chunks,
  onChange,
  disabled,
  onContextValidityChange,
}: {
  question: AgentStructuredQuestion;
  chunks: TextTemplateChunk[];
  onChange: (chunks: TextTemplateChunk[]) => void;
  disabled: boolean;
  onContextValidityChange: (valid: boolean) => void;
}) {
  const triggerPickerRef = useRef<(() => void) | null>(null);
  const canvasNodes = useNodes<ReactFlowNode>();
  const canvasEdges = useEdges<ReactFlowEdge>();
  const effectiveGraph = useMemo(() => {
    const fallback = { nodes: canvasNodes, edges: canvasEdges, error: false };
    try {
      const graphOpsVersion = readGraphOpsVersion(
        question.variable_context_graph_ops_version
      );
      if (!question.variable_context_ops?.length) return fallback;
      const result = applyGraphOps(
        parseCompilerRawOps(question.variable_context_ops, graphOpsVersion),
        canvasNodes,
        canvasEdges,
        { graphOpsVersion }
      );
      return result.kind === 'success'
        ? { nodes: result.nodes, edges: result.edges, error: false }
        : { ...fallback, error: true };
    } catch {
      return {
        ...fallback,
        error: true,
      };
    }
  }, [
    canvasEdges,
    canvasNodes,
    question.variable_context_ops,
    question.variable_context_graph_ops_version,
  ]);
  useEffect(() => {
    onContextValidityChange(!effectiveGraph.error);
  }, [effectiveGraph.error, onContextValidityChange]);

  return (
    <div className={styles.templateAnswer}>
      <button
        type='button'
        className={styles.variableButton}
        onClick={() => triggerPickerRef.current?.()}
        disabled={disabled || effectiveGraph.error}
        aria-label='Insert variable'
      >
        <BrackedAddIcon />
        <span>Insert variable</span>
      </button>
      {effectiveGraph.error ? (
        <span className={styles.phoneError} role='alert'>
          Copilot could not load the variables for this question. Refresh the
          workflow and try again.
        </span>
      ) : (
        <TemplatedTextArea
          nodeId={question.node_id as string}
          chunks={chunks}
          onChange={onChange}
          disabled={disabled}
          placeholder='Type your answer'
          bordered
          onTriggerPickerRef={(trigger) => {
            triggerPickerRef.current = trigger;
          }}
          pickerAsModal
          enableUserMentions={false}
          graphNodes={effectiveGraph.nodes}
          graphEdges={effectiveGraph.edges}
        />
      )}
    </div>
  );
}

export function ClarifyingQuestionCard({
  question,
  disabled = false,
  exiting = false,
  onSubmit,
  initialAnswer,
}: Props) {
  const cardRef = useRef<HTMLElement>(null);
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, [question.question_id]);
  const [selected, setSelected] = useState<string[]>(
    () =>
      initialAnswer?.values.filter((value) =>
        question.choices.some((choice) => choice.value === value)
      ) ??
      question.default_values ??
      []
  );
  const [other, setOther] = useState(
    () =>
      initialAnswer?.values
        .filter(
          (value) => !question.choices.some((choice) => choice.value === value)
        )
        .join(', ') ?? ''
  );
  const [templateChunks, setTemplateChunks] = useState<TextTemplateChunk[]>(
    () =>
      initialAnswer?.templateChunks ??
      (question.default_values?.[0]
        ? [{ text: question.default_values[0] }]
        : [])
  );
  const [templateContext, setTemplateContext] = useState<{
    question: AgentStructuredQuestion;
    valid: boolean;
  } | null>(null);
  const onTemplateContextValidityChange = useCallback(
    (valid: boolean) => {
      setTemplateContext({ question, valid });
    },
    [question]
  );
  const [query, setQuery] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(() => {
    const raw = initialAnswer?.values[0] ?? question.default_values?.[0] ?? '';
    const digits = raw.replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  });
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneErrorId = useId();
  const isSingle = question.kind === 'single_select';
  const isText = question.kind === 'text';
  const isDynamicTemplate =
    isText &&
    question.supports_dynamic_variables === true &&
    Boolean(question.node_id);
  const isPhone =
    isText && !isDynamicTemplate && question.input_type === 'phone';
  const validPhone =
    /^\+[1-9]\d{7,14}$/.test(phoneNumber) && isPossiblePhoneNumber(phoneNumber);
  const showPhoneError =
    isPhone && !validPhone && (phoneTouched || Boolean(phoneNumber));
  const isAIAgentPicker =
    isSingle &&
    (question.resource_kind === 'ai_agent' ||
      question.resource_kind === 'ai_agent_inbound');
  const isEmailRecipientPicker =
    question.kind === 'team_picker' && question.resource_kind === 'email';
  const hasQuestionProgress =
    question.question_index !== undefined &&
    question.question_count !== undefined;
  const questionCount = Math.max(1, question.question_count ?? 1);
  const questionIndex = Math.min(
    questionCount,
    Math.max(1, question.question_index ?? 1)
  );
  const isMultiQuestionFlow = hasQuestionProgress && questionCount > 1;
  const emailRecipientTeams = useMemo<TeamData[]>(() => {
    if (!isEmailRecipientPicker) return [];
    const teams = new Map<string, TeamData>();
    for (const choice of question.choices) {
      if (!choice.team_id) continue;
      const team = teams.get(choice.team_id) ?? {
        teamId: choice.team_id,
        teamName: choice.team_label ?? choice.label,
        color: choice.color ?? undefined,
        users: [],
      };
      if (
        choice.recipient_kind === 'user' &&
        choice.user_id &&
        !team.users.some((user) => user.id === choice.user_id)
      ) {
        team.users.push({
          id: choice.user_id,
          name: choice.label,
          email: choice.email ?? undefined,
          teamId: choice.team_id,
        });
      }
      teams.set(choice.team_id, team);
    }
    return [...teams.values()];
  }, [isEmailRecipientPicker, question.choices]);
  const emailRecipientSelection = useMemo<TeamUserSelection>(() => {
    const choicesByValue = new Map(
      question.choices.map((choice) => [choice.value, choice])
    );
    const teamIds: string[] = [];
    const userIds: string[] = [];
    for (const value of selected) {
      const choice = choicesByValue.get(value);
      if (choice?.recipient_kind === 'team' && choice.team_id) {
        teamIds.push(choice.team_id);
      } else if (choice?.recipient_kind === 'user' && choice.user_id) {
        userIds.push(choice.user_id);
      }
    }
    return { teamIds, userIds };
  }, [question.choices, selected]);
  const visibleChoices = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized
      ? question.choices.filter((choice) =>
          choice.label.toLowerCase().includes(normalized)
        )
      : question.choices;
  }, [query, question.choices]);
  const templateDisplayValue = templateChunks
    .map((chunk) => {
      if (chunk.text !== undefined) return chunk.text;
      if (!chunk.variable) return '';
      const name = chunk.variable.derived_variable_name
        ? `${chunk.variable.variable_name}.${chunk.variable.derived_variable_name}`
        : chunk.variable.variable_name;
      return `{{${chunk.variable.node_id}.${name}}}`;
    })
    .join('');
  const values = isPhone
    ? phoneNumber
      ? [phoneNumber]
      : []
    : isText
      ? other.trim()
        ? [other.trim()]
        : []
      : [...selected, ...(other.trim() ? [other.trim()] : [])];
  const canSubmit = isDynamicTemplate
    ? Boolean(templateDisplayValue.trim()) &&
      templateContext?.question === question &&
      templateContext.valid
    : values.length >= question.min_selections &&
      values.length <= question.max_selections &&
      (!isPhone || validPhone);

  const toggle = (value: string) => {
    if (isSingle) {
      setSelected([value]);
      setOther('');
      return;
    }
    setSelected((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : current.length < question.max_selections
          ? [...current, value]
          : current
    );
  };

  const updateEmailRecipients = (selection: TeamUserSelection) => {
    const teamValues = new Map(
      question.choices
        .filter((choice) => choice.recipient_kind === 'team' && choice.team_id)
        .map((choice) => [choice.team_id as string, choice.value])
    );
    const userValues = new Map(
      question.choices
        .filter((choice) => choice.recipient_kind === 'user' && choice.user_id)
        .map((choice) => [choice.user_id as string, choice.value])
    );
    setSelected([
      ...selection.teamIds.flatMap((teamId) => {
        const value = teamValues.get(teamId);
        return value ? [value] : [];
      }),
      ...selection.userIds.flatMap((userId) => {
        const value = userValues.get(userId);
        return value ? [value] : [];
      }),
    ]);
  };

  const renderChoice = (choice: AgentStructuredQuestion['choices'][number]) => (
    <label
      key={choice.value}
      className={`${styles.option} ${choice.disabled ? styles.disabled : ''}`}
    >
      {isSingle ? (
        <Radio
          checked={selected.includes(choice.value)}
          onChange={() => toggle(choice.value)}
          disabled={disabled || choice.disabled}
        />
      ) : (
        <Checkbox
          checked={selected.includes(choice.value)}
          onChange={() => toggle(choice.value)}
          disabled={disabled || choice.disabled}
        />
      )}
      {choice.status && (
        <span
          className={styles.statusDot}
          data-status={choice.status}
          // eslint-disable-next-line react/forbid-dom-props -- Workspace team colors are runtime values, not a fixed CSS palette.
          style={choice.color ? { backgroundColor: choice.color } : undefined}
        />
      )}
      <span className={styles.choiceLabel} title={choice.label}>
        {choice.label}
      </span>
      {choice.disabled ? (
        <small>No users</small>
      ) : choice.status === 'setup_required' && choice.can_setup ? (
        <a
          className={styles.setupLink}
          href='/settings/team'
          target='_blank'
          rel='noreferrer'
          onClick={(event) => event.stopPropagation()}
        >
          Set up
        </a>
      ) : null}
    </label>
  );

  const teamSections =
    question.kind === 'team_picker'
      ? [
          {
            label: 'Connected',
            choices: visibleChoices.filter(
              (choice) => choice.status !== 'setup_required'
            ),
          },
          {
            label: 'Need setup',
            choices: visibleChoices.filter(
              (choice) => choice.status === 'setup_required'
            ),
          },
        ].filter((section) => section.choices.length > 0)
      : [];

  return (
    <section
      ref={cardRef}
      tabIndex={-1}
      className={`${styles.card} ${exiting ? styles.cardExiting : ''}`}
      aria-label='Copilot question'
    >
      <strong>{question.prompt}</strong>
      <div className={`${styles.body} ${isPhone ? styles.phoneBody : ''}`}>
        {question.kind === 'team_picker' && !isEmailRecipientPicker && (
          <Input.Search
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder='Search teams'
            disabled={disabled}
          />
        )}
        {isDynamicTemplate ? (
          <DynamicTemplateAnswer
            question={question}
            chunks={templateChunks}
            onChange={setTemplateChunks}
            disabled={disabled}
            onContextValidityChange={onTemplateContextValidityChange}
          />
        ) : isPhone ? (
          <>
            <PhoneInput
              country='us'
              enableLongNumbers
              value={phoneNumber.replace(/^\+/, '')}
              onChange={(digits) => {
                setPhoneNumber(digits ? `+${digits}` : '');
                setPhoneTouched(true);
              }}
              onBlur={() => setPhoneTouched(true)}
              disabled={disabled}
              containerClass={styles.phoneInput}
              inputProps={{
                'aria-label': 'Phone number',
                'aria-invalid': showPhoneError || undefined,
                'aria-describedby': showPhoneError ? phoneErrorId : undefined,
                autoComplete: 'tel',
                required: question.min_selections > 0,
              }}
            />
            {showPhoneError && (
              <span
                id={phoneErrorId}
                className={styles.phoneError}
                role='alert'
              >
                Enter a complete phone number, including its country code.
              </span>
            )}
          </>
        ) : isText ? (
          <Input
            value={other}
            onChange={(event) => setOther(event.target.value)}
            disabled={disabled}
            placeholder='Type your answer'
          />
        ) : isAIAgentPicker ? (
          <Select
            aria-label='AI agent'
            showSearch
            optionFilterProp='label'
            value={selected[0]}
            onChange={(value: string) => {
              setSelected([value]);
              setOther('');
            }}
            placeholder='Select an AI agent'
            notFoundContent='No AI agents available'
            disabled={disabled}
            style={{ width: '100%' }}
          >
            {question.choices.length > 0 && (
              <Select.OptGroup
                label={
                  question.resource_kind === 'ai_agent_inbound'
                    ? 'Inbound'
                    : 'Outbound'
                }
              >
                {question.choices.map((choice) => (
                  <Select.Option
                    key={choice.value}
                    value={choice.value}
                    label={choice.label}
                    disabled={choice.disabled}
                  >
                    <span className={styles.aiAgentOption}>
                      {question.resource_kind === 'ai_agent_inbound' ? (
                        <span aria-hidden='true'>
                          <InboundIcon
                            size={14}
                            color='var(--text-secondary)'
                          />
                        </span>
                      ) : (
                        <span aria-hidden='true'>
                          <OutboundIcon
                            size={14}
                            color='var(--text-secondary)'
                          />
                        </span>
                      )}
                      {choice.label}
                    </span>
                  </Select.Option>
                ))}
              </Select.OptGroup>
            )}
          </Select>
        ) : isEmailRecipientPicker ? (
          <TeamUserSelect
            teamsData={emailRecipientTeams}
            additionalOptions={[]}
            value={emailRecipientSelection}
            onChange={updateEmailRecipients}
            disabled={disabled}
            placeholder='Select teams or people'
            openInline
          />
        ) : (
          <div className={styles.options}>
            {question.kind === 'team_picker'
              ? teamSections.map((section) => (
                  <div className={styles.section} key={section.label}>
                    <span className={styles.sectionLabel}>
                      {section.label} · {section.choices.length}
                    </span>
                    {section.choices.map(renderChoice)}
                  </div>
                ))
              : visibleChoices.map(renderChoice)}
            {question.kind === 'team_picker' && teamSections.length === 0 && (
              <span className={styles.emptyOptions}>
                {query.trim() ? 'No matching teams' : 'No teams available'}
              </span>
            )}
            {question.allow_other && (
              <div className={styles.option}>
                {isSingle ? (
                  <Radio
                    aria-label='Other'
                    checked={Boolean(other.trim())}
                    disabled={disabled}
                    onChange={() => setSelected([])}
                  />
                ) : (
                  <Checkbox
                    aria-label='Other'
                    checked={Boolean(other.trim())}
                    disabled={disabled}
                    onChange={(event) => {
                      if (!event.target.checked) setOther('');
                    }}
                  />
                )}
                <Input
                  className={styles.otherInput}
                  variant='borderless'
                  value={other}
                  onChange={(event) => {
                    setOther(event.target.value);
                    if (isSingle && event.target.value.trim()) setSelected([]);
                  }}
                  disabled={disabled}
                  placeholder='Type something'
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        {isMultiQuestionFlow ? (
          <span aria-label={`Question ${questionIndex} of ${questionCount}`}>
            {questionIndex} / {questionCount}
          </span>
        ) : !hasQuestionProgress && !isSingle && !isText ? (
          <span>
            {values.length} / {question.max_selections}
          </span>
        ) : null}
        <Button
          className={styles.submitButton}
          type='primary'
          size='small'
          disabled={disabled || !canSubmit}
          onClick={() =>
            isDynamicTemplate ? onSubmit([], templateChunks) : onSubmit(values)
          }
        >
          {isMultiQuestionFlow
            ? 'Continue'
            : hasQuestionProgress || question.kind === 'single_select' || isText
              ? 'Submit'
              : 'Continue'}
        </Button>
      </div>
    </section>
  );
}
